/**
 * B3 个人设置中心 5 tab
 *   /settings
 */
import { useEffect, useState } from 'react';
import { Input, Tabs, Switch, Slider, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { $, api } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { PageHeader, HomeCard, Btn, Pill, TableWrap, Avatar } from '../components/ui';
import { fmtMonthDayTime } from '../lib/format';
import { notificationSound } from '../utils/notificationSound';

export function SettingsPage() {
  const me = useAuthStore((s) => s.user)!;

  return (
    <>
      <PageHeader breadcrumb={['设置']} title="⚙️ 个人设置" subtitle={me.realName + ' · ' + me.role} />
      <div className="work-content">
        <Tabs
          defaultActiveKey="profile"
          items={[
            { key: 'profile',  label: '个人资料', children: <ProfileTab /> },
            { key: 'password', label: '修改密码', children: <PasswordTab /> },
            { key: 'notif',    label: '通知偏好', children: <NotifTab /> },
            { key: 'security', label: '安全设置', children: <SecurityTab /> },
            { key: 'about',    label: '关于',     children: <AboutTab /> },
          ]}
        />
      </div>
    </>
  );
}

/* ─── Tab 1: 个人资料 ─── */
function ProfileTab() {
  const qc = useQueryClient();
  const setStoreUser = useAuthStore((s) => s.setUser);
  const storeUser = useAuthStore((s) => s.user);
  const { data } = useQuery<any>({
    queryKey: ['me-settings'],
    queryFn: () => $.get('/me/settings'),
  });
  const profile = data?.profile;
  const [form, setForm] = useState({ realName: '', email: '' });
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile) {
      setForm({
        realName: profile.realName ?? '',
        email: profile.email ?? '',
      });
    }
  }, [profile]);

  const save = useMutation({
    mutationFn: () => $.put('/me/profile', form),
    onSuccess: () => {
      message.success('已保存');
      qc.invalidateQueries({ queryKey: ['me-settings'] });
    },
    onError: (e: any) => message.error(e?.message ?? '保存失败'),
  });

  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      setProgress(1);
      const r = await api.post('/me/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e: any) => {
          if (e?.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      return r.data as { avatar_url: string };
    },
    onSuccess: (r) => {
      setProgress(0);
      message.success('头像已更新');
      // 同步刷新 store user 让顶栏 / 其它 hooks 立即拿到新 URL
      if (storeUser) setStoreUser({ ...storeUser, avatarUrl: r.avatar_url });
      qc.invalidateQueries({ queryKey: ['me-settings'] });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (e: any) => {
      setProgress(0);
      message.error(e?.message ?? '上传失败');
    },
  });

  const onPickFile = (f: File) => {
    if (!f.type.startsWith('image/')) {
      message.error('请选择图片文件'); return;
    }
    if (f.size > 5 * 1024 * 1024) {
      message.error('图片不能超过 5MB'); return;
    }
    uploadAvatar.mutate(f);
  };

  // Ctrl+V 粘贴：监听全局 paste（只在本 tab mounted 时）
  useEffect(() => {
    const handler = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of Array.from(items)) {
        if (it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) {
            e.preventDefault();
            onPickFile(new File([f], `paste-${Date.now()}.png`, { type: f.type }));
            return;
          }
        }
      }
    };
    window.addEventListener('paste', handler);
    return () => window.removeEventListener('paste', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!profile) return <div style={{ padding: 24, color: 'var(--text-3)' }}>加载中…</div>;
  return (
    <HomeCard title="基础信息">
      <div style={{ padding: 24, maxWidth: 480 }}>
        <Row label="头像">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault(); setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onPickFile(f);
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 16,
              padding: 12, borderRadius: 8,
              border: '1.5px dashed ' + (dragOver ? 'var(--primary)' : 'var(--border-default)'),
              background: dragOver ? 'rgba(51,112,255,0.06)' : 'transparent',
              transition: 'all 120ms linear',
            }}
          >
            <Avatar userId={profile.id} src={profile.avatarUrl} name={profile.realName} size="lg" />
            <div style={{ flex: 1 }}>
              <Btn kind="primary" onClick={() => fileRef.current?.click()} disabled={uploadAvatar.isPending}>
                {uploadAvatar.isPending
                  ? (progress > 0 && progress < 100 ? `上传中 ${progress}%` : '上传中…')
                  : '📷 更换头像'}
              </Btn>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
                     style={{ display: 'none' }}
                     onChange={(e) => {
                       const f = e.target.files?.[0];
                       if (f) onPickFile(f);
                       e.target.value = '';
                     }} />
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
                支持 PNG / JPG / WEBP，最大 5MB · 点击 / 拖拽 / Ctrl+V 粘贴均可
              </div>
              {uploadAvatar.isPending && progress > 0 && (
                <div style={{
                  width: '100%', height: 4, background: 'var(--bg-2)',
                  borderRadius: 2, marginTop: 8, overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${progress}%`, height: '100%', background: 'var(--primary)',
                    transition: 'width 120ms linear',
                  }} />
                </div>
              )}
            </div>
          </div>
        </Row>
        <Row label="显示名">
          <Input value={form.realName} onChange={(e) => setForm({ ...form, realName: e.target.value })} />
        </Row>
        <Row label="手机号">
          <Input value={profile.phone} disabled
                 suffix={<span style={{ fontSize: 11, color: 'var(--text-3)' }}>不可修改</span>} />
        </Row>
        <Row label="邮箱">
          <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                 placeholder="可选 · 用于密码重置 + 邮件通知" />
        </Row>
        <Row label="角色">
          <Pill tone="default">{profile.role}</Pill>
        </Row>
        <div style={{ marginTop: 12 }}>
          <Btn kind="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? '保存中…' : '保存修改'}
          </Btn>
        </div>
      </div>
    </HomeCard>
  );
}

/* ─── Tab 2: 改密码 ─── */
function PasswordTab() {
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');

  const change = useMutation({
    mutationFn: () => $.post('/auth/change-password', { oldPassword: oldPw, newPassword: newPw }),
    onSuccess: () => {
      message.success('密码已修改 · 下次请用新密码登录');
      setOldPw(''); setNewPw(''); setConfirm('');
    },
    onError: (e: any) => message.error(e?.message ?? '修改失败'),
  });

  const strong = newPw.length >= 8 && /[a-zA-Z]/.test(newPw) && /[0-9]/.test(newPw);
  const match = newPw === confirm;
  const canSubmit = oldPw && newPw && confirm && strong && match;

  return (
    <HomeCard title="修改密码">
      <div style={{ padding: 24, maxWidth: 480 }}>
        <Row label="当前密码">
          <Input.Password value={oldPw} onChange={(e) => setOldPw(e.target.value)} />
        </Row>
        <Row label="新密码">
          <Input.Password value={newPw} onChange={(e) => setNewPw(e.target.value)}
                          placeholder="≥ 8 位 · 含字母 + 数字" />
          <div style={{ fontSize: 11, color: strong ? 'var(--success)' : 'var(--text-3)', marginTop: 4 }}>
            {strong ? '✓ 强度合格' : '· 至少 8 位含字母+数字'}
          </div>
        </Row>
        <Row label="确认新密码">
          <Input.Password value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {confirm && (
            <div style={{ fontSize: 11, color: match ? 'var(--success)' : 'var(--danger)', marginTop: 4 }}>
              {match ? '✓ 两次输入一致' : '✗ 两次输入不一致'}
            </div>
          )}
        </Row>
        <div style={{ marginTop: 12 }}>
          <Btn kind="primary" onClick={() => change.mutate()} disabled={!canSubmit || change.isPending}>
            {change.isPending ? '修改中…' : '确认修改'}
          </Btn>
        </div>
      </div>
    </HomeCard>
  );
}

/* ─── Tab 3: 通知偏好 ─── */
const SOUND_PRESETS = [
  { key: 'ding', label: '🔔 清亮叮', hint: '默认' },
  { key: 'dingdong', label: '🛎️ 叮咚双音', hint: '' },
  { key: 'tritone', label: '🎵 清脆三音', hint: '' },
  { key: 'pop', label: '💬 微信式', hint: '' },
  { key: 'message', label: '🎐 经典铃声', hint: '' },
];

function NotifTab() {
  const qc = useQueryClient();
  const { data } = useQuery<any>({
    queryKey: ['me-settings'],
    queryFn: () => $.get('/me/settings'),
  });
  const prefs = data?.notification_preferences;

  const save = useMutation({
    mutationFn: (next: any) => $.put('/me/notification-preferences', next),
    onSuccess: () => {
      message.success('已更新');
      qc.invalidateQueries({ queryKey: ['me-settings'] });
    },
    onError: (e: any) => message.error(e?.message ?? '更新失败'),
  });

  // 提示音偏好（权威存后端 settings_json.im_sound，跨设备同步）
  const [snd, setSnd] = useState<{ enabled: boolean; volume: number; preset: string; customUrl: string | null }>({
    enabled: notificationSound.isEnabled(),
    volume: notificationSound.getVolume(),
    preset: notificationSound.getPreset(),
    customUrl: notificationSound.getCustomUrl(),
  });
  const [uploading, setUploading] = useState(false);
  const soundFileRef = useRef<HTMLInputElement>(null);

  // 后端偏好到达 → 同步本地 state + 播放器
  useEffect(() => {
    const s = data?.im_sound;
    if (s) {
      setSnd({ enabled: !!s.enabled, volume: Number(s.volume ?? 0.6), preset: s.preset || 'ding', customUrl: s.customUrl ?? null });
      notificationSound.configure(s);
    }
  }, [data]);

  // 写后端提示音偏好（enabled / volume / preset）
  const saveSound = useMutation({
    mutationFn: (patch: any) => $.put('/me/notification-sound', patch),
    onError: (e: any) => message.error(e?.message ?? '保存失败'),
  });

  const setSoundEnabled = (enabled: boolean) => {
    setSnd((p) => ({ ...p, enabled }));
    notificationSound.setEnabled(enabled);
    saveSound.mutate({ enabled });
    if (enabled) notificationSound.preview();
  };
  const pickPreset = (preset: string) => {
    setSnd((p) => ({ ...p, preset }));
    notificationSound.setPreset(preset, snd.customUrl);
    saveSound.mutate({ preset });
    if (preset === 'custom') { if (snd.customUrl) notificationSound.previewSrc(snd.customUrl, snd.volume); }
    else notificationSound.previewSrc(`/sounds/${preset}.wav`, snd.volume);
  };

  const onPickFile = async (file: File) => {
    const okType = /(audio\/(mpeg|mp3|wav|x-wav|wave|vnd\.wave))/i.test(file.type) || /\.(mp3|wav)$/i.test(file.name);
    if (!okType) { message.error('只支持 mp3 / wav 格式'); return; }
    if (file.size > 500 * 1024) { message.error(`文件 ${(file.size / 1024).toFixed(0)}KB 超过 500KB 上限`); return; }
    const dur = await new Promise<number>((res) => {
      const a = new Audio();
      a.preload = 'metadata';
      a.onloadedmetadata = () => res(a.duration);
      a.onerror = () => res(-1);
      a.src = URL.createObjectURL(file);
    });
    if (dur > 3.3) { message.error(`时长 ${dur.toFixed(1)} 秒超过 3 秒上限`); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/me/notification-sound', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const s = r.data?.im_sound;
      if (s) {
        setSnd({ enabled: !!s.enabled, volume: Number(s.volume ?? 0.6), preset: s.preset || 'custom', customUrl: s.customUrl ?? null });
        notificationSound.configure(s);
        if (s.customUrl) notificationSound.previewSrc(s.customUrl, Number(s.volume ?? 0.6));
      }
      message.success('自定义铃声已上传');
      qc.invalidateQueries({ queryKey: ['me-settings'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? e?.message ?? '上传失败');
    } finally {
      setUploading(false);
      if (soundFileRef.current) soundFileRef.current.value = '';
    }
  };

  const delCustom = useMutation({
    mutationFn: async () => { const r = await api.delete('/me/notification-sound/custom'); return r.data; },
    onSuccess: (r: any) => {
      const s = r?.im_sound;
      if (s) { setSnd((p) => ({ ...p, preset: s.preset, customUrl: null })); notificationSound.configure(s); }
      message.success('已删除自定义铃声');
      qc.invalidateQueries({ queryKey: ['me-settings'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '删除失败'),
  });

  if (!prefs) return <div style={{ padding: 24, color: 'var(--text-3)' }}>加载中…</div>;
  const toggle = (k: string) => save.mutate({ ...prefs, [k]: !prefs[k] });

  const presetOptions = [...SOUND_PRESETS, ...(snd.customUrl ? [{ key: 'custom', label: '🎤 我的自定义', hint: '' }] : [])];

  return (
    <HomeCard title="通知通道">
      <div style={{ padding: 24, maxWidth: 560 }}>
        <NotifRow
          title="🔔 消息提示音" desc="收到新消息播放铃声（@我 连播 2 次，静音会话不响，1 秒内多条只响一次）"
          checked={snd.enabled}
          onChange={() => setSoundEnabled(!snd.enabled)}
        />
        {snd.enabled && (
          <div style={{ padding: '12px 0 14px 0', borderBottom: '1px solid var(--border-default)' }}>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8 }}>🎼 铃声（点击试听并选中）</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              {presetOptions.map((opt) => {
                const active = snd.preset === opt.key;
                return (
                  <div key={opt.key}
                    onClick={() => pickPreset(opt.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                      padding: '6px 12px', borderRadius: 6, fontSize: 13, userSelect: 'none',
                      border: `1px solid ${active ? 'var(--primary, #1677ff)' : 'var(--border-default)'}`,
                      background: active ? 'var(--primary-bg, rgba(22,119,255,0.08))' : 'var(--surface)',
                      color: active ? 'var(--primary, #1677ff)' : 'var(--text-2)',
                    }}>
                    <span>{opt.label}</span>
                    {opt.hint && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{opt.hint}</span>}
                    {active && <span>✓</span>}
                    {opt.key === 'custom' && (
                      <span
                        onClick={(ev) => { ev.stopPropagation(); delCustom.mutate(); }}
                        title="删除自定义铃声"
                        style={{ marginLeft: 4, color: 'var(--danger, #ff4d4f)', fontWeight: 600 }}>✕</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ marginBottom: 14 }}>
              <input ref={soundFileRef} type="file" accept="audio/mpeg,audio/wav,.mp3,.wav" style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onPickFile(f); }} />
              <button
                onClick={() => soundFileRef.current?.click()}
                disabled={uploading}
                style={{
                  padding: '4px 14px', fontSize: 12, cursor: uploading ? 'wait' : 'pointer',
                  border: '1px solid var(--border)', borderRadius: 4,
                  background: 'var(--surface)', color: 'var(--text-2)',
                }}>
                {uploading ? '上传中…' : '⬆️ 上传自定义铃声'}
              </button>
              <span style={{ marginLeft: 10, fontSize: 11, color: 'var(--text-3)' }}>mp3 / wav，≤ 500KB，≤ 3 秒</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ fontSize: 13, color: 'var(--text-2)', minWidth: 56 }}>🔈 音量</span>
              <Slider
                min={0} max={100}
                value={Math.round(snd.volume * 100)}
                onChange={(v: number) => { const vol = v / 100; setSnd((p) => ({ ...p, volume: vol })); notificationSound.setVolume(vol); }}
                onChangeComplete={(v: number) => { const vol = v / 100; notificationSound.setVolume(vol); saveSound.mutate({ volume: vol }); notificationSound.preview(); }}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 12, color: 'var(--text-3)', minWidth: 36, textAlign: 'right' }}>
                {Math.round(snd.volume * 100)}%
              </span>
            </div>
          </div>
        )}
        <NotifRow
          title="💬 IM 系统消息" desc="客户告警 / 审批 / 业绩 / 投流分配 等所有业务通知 — 推荐保持开启"
          checked={prefs.im} disabled
          onChange={() => {}}
        />
        <NotifRow
          title="📱 钉钉冗余推送" desc="所有 IM 通知同步推到钉钉移动端（不在 CRM 时也能看）"
          checked={prefs.dingtalk}
          onChange={() => toggle('dingtalk')}
        />
        <NotifRow
          title="✉️ 邮件通知" desc="重要事件邮件抄送（需先在'个人资料'填写邮箱）"
          checked={prefs.email}
          onChange={() => toggle('email')}
        />
      </div>
    </HomeCard>
  );
}

function NotifRow({ title, desc, checked, onChange, disabled, extra }: any) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '14px 0', borderBottom: '1px solid var(--border-default)',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, color: 'var(--text-1)', marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{desc}</div>
      </div>
      {extra}
      <Switch checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

/* ─── Tab 4: 安全设置 ─── */
function SecurityTab() {
  const qc = useQueryClient();
  const { data: sessions } = useQuery<any[]>({
    queryKey: ['me-sessions'],
    queryFn: () => $.get('/me/sessions'),
  });
  const logoutAll = useMutation({
    mutationFn: () => $.post('/me/logout-all', {}),
    onSuccess: (r: any) => {
      message.success(`已撤销 ${r.revoked} 个 session，重新登录即可`);
      qc.invalidateQueries({ queryKey: ['me-sessions'] });
    },
    onError: (e: any) => message.error(e?.message ?? '操作失败'),
  });

  return (
    <>
      <HomeCard title={`登录设备（${sessions?.length ?? 0}）`}>
        <TableWrap>
          <table className="ck-table" style={{ width: '100%' }}>
            <thead>
              <tr><th>IP</th><th>User-Agent</th><th>登录时间</th><th>最近活跃</th><th>过期</th></tr>
            </thead>
            <tbody>
              {(sessions ?? []).length === 0 && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>
                  暂无活跃 session
                </td></tr>
              )}
              {(sessions ?? []).map((s: any) => (
                <tr key={s.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{s.ipAddress ?? '—'}</td>
                  <td style={{ fontSize: 11, color: 'var(--text-3)', maxWidth: 240, whiteSpace: 'normal' }}>
                    {(s.userAgent ?? '').slice(0, 80)}
                  </td>
                  <td>{s.createdAt ? fmtMonthDayTime(s.createdAt) : '—'}</td>
                  <td>{s.lastUsedAt ? fmtMonthDayTime(s.lastUsedAt) : '—'}</td>
                  <td>{s.expiresAt ? fmtMonthDayTime(s.expiresAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
        <div style={{ padding: 16, borderTop: '1px solid var(--border-default)' }}>
          <Btn kind="default" onClick={() => logoutAll.mutate()} disabled={logoutAll.isPending}>
            {logoutAll.isPending ? '撤销中…' : '🚪 退出所有设备'}
          </Btn>
          <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--text-3)' }}>
            会失效所有 refresh token，需重新登录所有设备（含当前）
          </span>
        </div>
      </HomeCard>

      <div style={{ marginTop: 16 }}>
        <HomeCard title="二次验证（2FA）">
          <div style={{ padding: 24, color: 'var(--text-3)', fontSize: 13 }}>
            🔐 双因素认证（TOTP / 短信） · v1.1 即将上线
          </div>
        </HomeCard>
      </div>
    </>
  );
}

/* ─── Tab 5: 关于 ─── */
function AboutTab() {
  return (
    <HomeCard title="关于 HuanKe-CRM">
      <div style={{ padding: 24, fontSize: 14, lineHeight: 1.8 }}>
        <div><strong>版本：</strong>{(import.meta as any).env?.VITE_APP_VERSION ?? 'v1.2.0-rc2'}</div>
        <div><strong>编译：</strong>{new Date().toISOString().slice(0, 10)}</div>
        <div style={{ marginTop: 16, fontSize: 13, color: 'var(--text-2)' }}>
          内部 CRM · 6 角色（admin / director / manager / sales /
          academic / academic_director）· 完整覆盖客户生命周期 L0→L8 +
          教务 9 阶段 · 含 IM / 投流 ROI / 群发话术 / 智能分配 / 教务模块
        </div>
        <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
          <Btn kind="default" onClick={() => window.open('/docs/api', '_blank')}>📘 API 文档</Btn>
          <Btn kind="default" onClick={() => window.open('mailto:feedback@huanke.com', '_blank')}>📮 提交反馈</Btn>
        </div>
      </div>
    </HomeCard>
  );
}

function Row({ label, children }: { label: string; children: any }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
